import { useState, useEffect, useRef } from 'react';
import type { Book, BookRecord, BookRecordCreate, Shelf } from 'types';
import { bookRecordService } from 'services/bookRecordService';
import { shelfService } from 'services/shelfService';
import { API_URL } from 'utils/apiUrl';
import './BookRecordModals.css';

interface IssueBookModalProps {
  books: Book[];
  existingRecords: BookRecord[];
  onClose: () => void;
  onSaved: (record: BookRecord) => void;
  preSelectedBook?: Book | null;
}

export const IssueBookModal = ({ books, existingRecords, onClose, onSaved, preSelectedBook }: IssueBookModalProps) => {
  const [selectedBookId, setSelectedBookId] = useState<number | ''>(preSelectedBook?.id || '');
  const [borrowerName, setBorrowerName] = useState('');
  const [contactInfo, setContactInfo] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [borrowerNames, setBorrowerNames] = useState<string[]>([]);
  const [showBorrowerDropdown, setShowBorrowerDropdown] = useState(false);
  const [filteredBorrowerNames, setFilteredBorrowerNames] = useState<string[]>([]);
  const borrowerInputRef = useRef<HTMLInputElement>(null);

  // Book search state
  const [bookSearchTerm, setBookSearchTerm] = useState('');
  const [showBookDropdown, setShowBookDropdown] = useState(false);
  const [filteredBooks, setFilteredBooks] = useState<Book[]>([]);
  const bookInputRef = useRef<HTMLInputElement>(null);
  const bookDropdownRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (preSelectedBook) {
      setBookSearchTerm(`${preSelectedBook.title} by ${preSelectedBook.author_name}`);
      setSelectedBookId(preSelectedBook.id);
    }
  }, [preSelectedBook]);

  useEffect(() => {
    loadBorrowerNames();
  }, []);

  useEffect(() => {
    // Filter books based on search term
    const availableBooks = books.filter((b) => {
      const isBorrowed = existingRecords.some(
        (r) => r.book_id === b.id && r.status === 'borrowed'
      );
      return !isBorrowed;
    });

    if (bookSearchTerm.trim() === '') {
      setFilteredBooks(availableBooks.slice(0, 10));
    } else {
      const filtered = availableBooks.filter(
        (book) =>
          book.title.toLowerCase().includes(bookSearchTerm.toLowerCase()) ||
          book.author_name.toLowerCase().includes(bookSearchTerm.toLowerCase()) ||
          book.book_id.toLowerCase().includes(bookSearchTerm.toLowerCase()) ||
          (book.isbn && book.isbn.includes(bookSearchTerm))
      );
      setFilteredBooks(filtered.slice(0, 10));
    }
  }, [bookSearchTerm, books, existingRecords]);

  const loadBorrowerNames = async () => {
    try {
      const names = await bookRecordService.getBorrowerNames();
      setBorrowerNames(names);
    } catch (err) {
      console.error('Error loading borrower names:', err);
    }
  };

  useEffect(() => {
    if (borrowerName.trim() === '') {
      setFilteredBorrowerNames(borrowerNames);
    } else {
      setFilteredBorrowerNames(
        borrowerNames.filter((name) =>
          name.toLowerCase().includes(borrowerName.toLowerCase())
        )
      );
    }
  }, [borrowerName, borrowerNames]);

  const handleBookSelect = (book: Book) => {
    setSelectedBookId(book.id);
    setBookSearchTerm(`${book.title} by ${book.author_name}`);
    setShowBookDropdown(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!selectedBookId) {
      setError('Please select a book');
      return;
    }
    if (!borrowerName) {
      setError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const recordData: BookRecordCreate = {
        book_id: Number(selectedBookId),
        borrower_name: borrowerName,
        contact_info: contactInfo || undefined,
        status: 'borrowed',
        notes: notes || undefined,
      };
      const record = await bookRecordService.createRecord(recordData);
      onSaved(record);
    } catch (err) {
      console.error('Error issuing book:', err);
      setError('Failed to issue book. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedBook = books.find((b) => b.id === selectedBookId);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content issue-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Issue Book</h2>
          <button className="modal-close" onClick={onClose}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            {error && <div className="alert alert-error">{error}</div>}

            <div className="form-group">
              <label htmlFor="bookSearch" className="form-label">
                Select Book <span className="required">*</span>
              </label>
              <div className="combobox-container">
                <input
                  type="text"
                  id="bookSearch"
                  ref={bookInputRef}
                  className="form-input"
                  value={bookSearchTerm}
                  onChange={(e) => {
                    setBookSearchTerm(e.target.value);
                    setShowBookDropdown(true);
                    if (e.target.value.trim() === '') {
                      setSelectedBookId('');
                    }
                  }}
                  onFocus={() => setShowBookDropdown(true)}
                  onBlur={() => setTimeout(() => setShowBookDropdown(false), 200)}
                  placeholder="Search by title, author, book ID, or ISBN..."
                  required
                />
                {showBookDropdown && (
                  <ul ref={bookDropdownRef} className="combobox-dropdown book-dropdown">
                    {filteredBooks.length > 0 ? (
                      filteredBooks.map((book) => (
                        <li
                          key={book.id}
                          className="combobox-option book-option"
                          onClick={() => handleBookSelect(book)}
                        >
                          <div className="book-option-content">
                            <span className="book-option-title">{book.title}</span>
                            <span className="book-option-author">by {book.author_name}</span>
                            <span className="book-option-meta">
                              {book.book_id} {book.isbn ? `• ISBN: ${book.isbn}` : ''}
                            </span>
                          </div>
                        </li>
                      ))
                    ) : (
                      <li className="combobox-option no-results">
                        {books.length === 0 ? 'No books available' : 'No matching books found'}
                      </li>
                    )}
                  </ul>
                )}
              </div>
            </div>

            {selectedBookId && (
              <div className="book-detail-preview">
                <div className="book-detail-grid">
                  <div className="book-detail-cover">
                    {selectedBook?.image_path ? (
                      <img src={`${API_URL}${selectedBook.image_path}`} alt={selectedBook.title} />
                    ) : (
                      <div className="book-detail-cover-placeholder">
                        <span className="material-symbols-outlined">menu_book</span>
                      </div>
                    )}
                  </div>
                  <div className="book-detail-info">
                    <div className="book-detail-row">
                      <span className="book-detail-label">Title:</span>
                      <span className="book-detail-value">{selectedBook?.title}</span>
                    </div>
                    <div className="book-detail-row">
                      <span className="book-detail-label">Author:</span>
                      <span className="book-detail-value">{selectedBook?.author_name}</span>
                    </div>
                    <div className="book-detail-row">
                      <span className="book-detail-label">Category:</span>
                      <span className="book-detail-value">{selectedBook?.category || 'N/A'}</span>
                    </div>
                    <div className="book-detail-row">
                      <span className="book-detail-label">ISBN:</span>
                      <span className="book-detail-value">{selectedBook?.isbn || 'N/A'}</span>
                    </div>
                    <div className="book-detail-row">
                      <span className="book-detail-label">Publisher:</span>
                      <span className="book-detail-value">{selectedBook?.publisher || 'N/A'}</span>
                    </div>
                    <div className="book-detail-row">
                      <span className="book-detail-label">Publication Year:</span>
                      <span className="book-detail-value">{selectedBook?.publication_year || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="borrowerName" className="form-label">
                Borrower Name <span className="required">*</span>
              </label>
              <div className="combobox-container">
                <input
                  type="text"
                  id="borrowerName"
                  ref={borrowerInputRef}
                  className="form-input"
                  value={borrowerName}
                  onChange={(e) => {
                    setBorrowerName(e.target.value);
                    setShowBorrowerDropdown(true);
                  }}
                  onFocus={() => setShowBorrowerDropdown(true)}
                  onBlur={() => setTimeout(() => setShowBorrowerDropdown(false), 200)}
                  placeholder="Enter or select borrower's name"
                  required
                />
                {showBorrowerDropdown && (
                  <ul className="combobox-dropdown">
                    {filteredBorrowerNames.length > 0 ? (
                      filteredBorrowerNames.map((name) => (
                        <li
                          key={name}
                          className="combobox-option"
                          onClick={() => {
                            setBorrowerName(name);
                            setShowBorrowerDropdown(false);
                          }}
                        >
                          {name}
                        </li>
                      ))
                    ) : (
                      <li className="combobox-option no-results">
                        {borrowerNames.length === 0 ? 'No previous borrowers' : 'No matching names'}
                      </li>
                    )}
                  </ul>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="contactInfo" className="form-label">
                Contact Info
              </label>
              <input
                type="text"
                id="contactInfo"
                className="form-input"
                value={contactInfo}
                onChange={(e) => setContactInfo(e.target.value)}
                placeholder="Phone number or email (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="notes" className="form-label">
                Notes
              </label>
              <textarea
                id="notes"
                className="form-textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>

            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Issuing...' : 'Issue Book'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Return Book Modal
interface ReturnBookModalProps {
  record: BookRecord;
  books?: Book[];
  onClose: () => void;
  onSaved: (record: BookRecord) => void;
}

export const ReturnBookModal = ({ record, books, onClose, onSaved }: ReturnBookModalProps) => {
  const [returnDate, setReturnDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState(record.notes || '');
  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [selectedShelfId, setSelectedShelfId] = useState<number | ''>('');
  const [isLoadingShelves, setIsLoadingShelves] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadShelves();
  }, []);

  // Set the current shelf when books are loaded
  useEffect(() => {
    if (books && books.length > 0) {
      const book = books.find(b => b.id === record.book_id);
      if (book && book.shelf_id) {
        setSelectedShelfId(book.shelf_id);
      }
    }
  }, [books, record.book_id]);

  const loadShelves = async () => {
    try {
      setIsLoadingShelves(true);
      // Get all shelves including empty ones for assignment
      const shelvesData = await shelfService.getAllShelvesIncludingEmpty({ limit: 1000 });
      setShelves(shelvesData);
    } catch (err) {
      console.error('Error loading shelves:', err);
      setError('Failed to load shelves. Please try again.');
    } finally {
      setIsLoadingShelves(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    setIsSubmitting(true);
    try {
      // Convert empty string to -1 to indicate "remove shelf assignment"
      let shelfIdToSend: number | undefined;
      if (selectedShelfId === '') {
        shelfIdToSend = -1;  // Explicitly remove shelf
      } else {
        shelfIdToSend = Number(selectedShelfId);
      }
      
      const updatedRecord = await bookRecordService.returnBook(record.id, returnDate, notes, shelfIdToSend);
      onSaved(updatedRecord);
    } catch (err) {
      console.error('Error returning book:', err);
      setError('Failed to return book. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content return-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Return Book</h2>
          <button className="modal-close" onClick={onClose}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            {error && <div className="alert alert-error">{error}</div>}

            <div className="book-info-summary">
            <p>
              <strong>Borrower:</strong> {record.borrower_name}
            </p>
            <p>
              <strong>Borrowed Date:</strong> {formatDate(record.borrowed_date)}
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="returnDate" className="form-label">
              Return Date <span className="required">*</span>
            </label>
            <input
              type="date"
              id="returnDate"
              className="form-input"
              value={returnDate}
              onChange={(e) => setReturnDate(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="shelf" className="form-label">
              Assign to Shelf
            </label>
            {isLoadingShelves ? (
              <div className="loading-shelves">
                <span className="material-symbols-outlined spinning">progress_activity</span>
                Loading shelves...
              </div>
            ) : shelves.length === 0 ? (
              <div className="no-shelves-message">
                <span className="material-symbols-outlined">shelves</span>
                <p>No shelves available</p>
                <p className="help-text">Create a shelf from the Shelf Management page first</p>
              </div>
            ) : (
              <>
                <select
                  id="shelf"
                  className="form-input"
                  value={selectedShelfId}
                  onChange={(e) => setSelectedShelfId(e.target.value ? Number(e.target.value) : '')}
                >
                  <option value="">No Shelf (Unassigned)</option>
                  {shelves.map((shelf) => (
                    <option key={shelf.id} value={shelf.id}>
                      {shelf.shelf_number} {shelf.book_count > 0 ? `(${shelf.book_count} books)` : ''}
                    </option>
                  ))}
                </select>
                <p className="form-help">Optionally assign this book to a shelf when returning</p>
              </>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              Notes
            </label>
            <textarea
              id="notes"
              className="form-textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes..."
              rows={3}
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Processing...' : 'Return Book'}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};
